import { getActiveSchedule, getScheduleHourBounds } from "@app/_helpers/utils";
import { UserSettingsQuery } from "@timeghost/web-api";
import { addMinutes, isSameDay, startOfDay } from "date-fns/esm";
import { CalendarDayData } from "../timer-calendar-view.service";
import {
	buildProjecthourSegments,
	buildWorkinghourSegments,
	getTotalSegmentCount,
} from "./timer-calendar-segment.builder";
import {
	CalendarDayViewModel,
	CalendarEnabledTimes,
	CalendarLayoutSettings,
	CalendarViewModel,
	HoursSegmentPresets,
	SegmentLayoutContext,
} from "./timer-calendar-view.models";

const DEFAULT_DAY_ROW_BASE_HEIGHT_PX = 128;
export const COMPACT_DAY_ROW_BASE_HEIGHT_PX = 96;

export function resolveDayHours(layout: CalendarLayoutSettings): { dayStartHour: number; dayEndHour: number } {
	if (layout.fromWorkDay) {
		return { dayStartHour: 6, dayEndHour: 24 };
	}
	if (layout.fromSchedule && layout.scheduleHourBounds) {
		const { startHour, endHour } = layout.scheduleHourBounds;
		if (startHour >= endHour) {
			return { dayStartHour: startHour, dayEndHour: 24 };
		}
		return { dayStartHour: startHour, dayEndHour: endHour };
	}
	return { dayStartHour: 0, dayEndHour: 24 };
}

export function getDayRowMinHeightPx(visibleDayCount: number, baseHeightPx = DEFAULT_DAY_ROW_BASE_HEIGHT_PX): number {
	const count = Math.max(visibleDayCount, 1) + 1;
	return Math.round((7 / count) * baseHeightPx);
}

export function resolveSegmentStartTime(
	date: Date,
	index: number,
	dayStartHour: number,
	segmentsPerHour: number
): Date {
	const segmentDuration = 60 / segmentsPerHour;
	return addMinutes(startOfDay(date), dayStartHour * 60 + index * segmentDuration);
}

export function resolveSegmentEndTime(date: Date, index: number, dayStartHour: number, segmentsPerHour: number): Date {
	const segmentDuration = 60 / segmentsPerHour;
	return addMinutes(startOfDay(date), dayStartHour * 60 + (index + 1) * segmentDuration);
}

export function resolveSegmentIndexFromTime(
	date: Date,
	time: Date,
	dayStartHour: number,
	dayEndHour: number,
	segmentsPerHour: number
): number {
	const segmentDuration = 60 / segmentsPerHour;
	const dayStart = addMinutes(startOfDay(date), dayStartHour * 60);
	const minutesFromStart = (time.getTime() - dayStart.getTime()) / (60 * 1000);
	const totalSegmentCount = (dayEndHour - dayStartHour) * segmentsPerHour;
	const index = Math.floor(minutesFromStart / segmentDuration);
	return Math.max(0, Math.min(totalSegmentCount - 1, index));
}

export function isHourBoundary(seg: number, segmentsPerHour: number): boolean {
	return seg % segmentsPerHour === 0;
}

export function rawHourForSegment(seg: number, dayStartHour: number, segmentsPerHour: number): number {
	return dayStartHour + seg / segmentsPerHour;
}

export function shouldShowHourLabel(
	seg: number,
	layout: CalendarLayoutSettings,
	dayStartHour: number,
	dayEndHour: number,
	segmentsPerHour: number,
	totalSegmentCount: number
): boolean {
	if (isHourBoundary(seg, segmentsPerHour)) {
		return rawHourForSegment(seg, dayStartHour, segmentsPerHour) < 24;
	}
	return layout.fromSchedule && seg === totalSegmentCount - 1;
}

export function isHourLabelAtEnd(
	seg: number,
	layout: CalendarLayoutSettings,
	segmentsPerHour: number,
	totalSegmentCount: number
): boolean {
	return layout.fromSchedule && seg === totalSegmentCount - 1 && !isHourBoundary(seg, segmentsPerHour);
}

export function hourLabelForSegment(
	seg: number,
	layout: CalendarLayoutSettings,
	dayStartHour: number,
	dayEndHour: number,
	segmentsPerHour: number,
	totalSegmentCount: number
): number {
	const isLast = seg === totalSegmentCount - 1;
	const hour = layout.fromSchedule && isLast ? dayEndHour : rawHourForSegment(seg, dayStartHour, segmentsPerHour);
	return Math.min(23, hour);
}

export function buildCalendarViewModel(
	days: CalendarDayData[],
	layout: CalendarLayoutSettings,
	enabledTimes: CalendarEnabledTimes,
	projectRowsCollapsedByDay: Map<number, boolean>,
	getProjectColor: (projectId?: string) => string | undefined
): CalendarViewModel {
	const { dayStartHour, dayEndHour } = resolveDayHours(layout);
	const segmentsPerHour = layout.preset;
	const segmentWidthPx = layout.segmentWidthPx;
	const hourLabelOffsetPx = segmentWidthPx / 2;
	const totalSegmentCount = (dayEndHour - dayStartHour) * segmentsPerHour;
	const timelineWidthPx = totalSegmentCount * segmentWidthPx;
	const segmentLayout: SegmentLayoutContext = {
		dayStartHour,
		dayEndHour,
		segmentsPerHour,
		timelineWidthPx,
		getProjectColor,
	};
	const visibleDayCount = days.length;
	const today = new Date();

	const dayViewModels: CalendarDayViewModel[] = days.map((day) => {
		const dayKey = day.date.getTime();
		const projectRows = buildProjecthourSegments(segmentLayout, day);
		const { segments: workingSegments, rangeFills: workingRangeFills } = buildWorkinghourSegments(
			segmentLayout,
			day
		);
		const collapsed = projectRowsCollapsedByDay.get(dayKey) ?? false;
		const visibleProjectRowCount = collapsed ? 1 : Math.min(4, projectRows.length);
		const rowBaseHeightPx = layout.dayRowBaseHeightPx ?? DEFAULT_DAY_ROW_BASE_HEIGHT_PX;

		return {
			dayKey,
			date: day.date,
			isToday: isSameDay(day.date, today),
			rowMinHeightPx: getDayRowMinHeightPx(visibleDayCount, rowBaseHeightPx),
			projectRows,
			projectRowsCollapsed: collapsed,
			visibleProjectRowCount,
			workingSegments,
			workingRangeFills,
			projecthoursTimes: day.projecthoursTimes,
			workinghoursTimes: day.workinghoursTimes,
		};
	});

	const todayIndex = dayViewModels.findIndex((d) => d.isToday);
	const rowBaseHeightPx = layout.dayRowBaseHeightPx ?? DEFAULT_DAY_ROW_BASE_HEIGHT_PX;
	const dayRowHeightPx = getDayRowMinHeightPx(visibleDayCount, rowBaseHeightPx);

	return {
		days: dayViewModels,
		segmentWidthPx,
		hourLabelOffsetPx,
		timelineWidthPx,
		visibleDayCount,
		enabledTimes,
		segmentIndices: Array.from({ length: totalSegmentCount }, (_, i) => i),
		dayStartHour,
		dayEndHour,
		segmentsPerHour,
		startFromSchedule: layout.fromSchedule,
		todayIndex,
		dayRowHeightPx,
	};
}

export function getScheduleHourBoundsForRange(
	userSettingsQuery: UserSettingsQuery,
	rangeStart: Date | undefined
): { startHour: number; endHour: number } | null {
	const user = userSettingsQuery.getValue();
	if (!user) return null;
	return getScheduleHourBounds(getActiveSchedule(user, rangeStart));
}
